import {
    CreateAutoVisitMutationVars,
    GetAutoVisitDocument,
    GetAutoVisitQueryResult,
    GetAutoVisitQueryVars,
    GetCheckPointListDocument,
    GetCheckpointListResult,
    GetCheckPointListVars,
    GetCheckPointPathBuilder,
    GetServiceListQueryResult,
    GetServiceListQueryVars,
    GetServiceListDocument,
    GetAutoVisitListDocument,
    GetAutoVisitsQueryResult,
    GetAutoVisitsQueryVars,
    CreateAutoVisitMutationResult,
    CreateAutoVisitDocument,
    UpdateAutoVisitQueryResult,
    UpdateAutoVisitQueryVars,
    UpdateAutoVisitDocument,
    CancelAutoVisitResult,
    CancelAutoVisitDocument,
    CancelAutoVisitVars,
    GetTimeSlotsDocument,
    GetTimeSlotsQueryVars,
    GetTimeSlotsQueryResult,
    GetCargoListResult,
    GetCargoListVars,
    GetCargoListDocument,
    AutoVisit,
    GetVehiclesDocument,
    GetVehiclesQueryVars,
    GetVehiclesQueryResult,
    GetDriversQueryResult,
    GetDriversQueryVars,
    GetDriversDocument,
    GetTimeSlotsPathBuilder, GetServiceListPathBuilder, autoVisitsListPathBuilder, GetCargoListPathBuilder, CargoV2
} from "./queries"
import {useCallback, useEffect, useMemo} from "react";
import {tuple} from "~/types";
import {ServerError, useApolloClient, useMutation} from "@apollo/client";
import {useQuery} from "~/apollo/patches";
import {useCurrentUser} from "~/auth/hooks";
import {useErrorHandler} from "~/components/ErrorHandling";
import moment from "moment";


export type useAutoVisitListOptions = {
    carrierId: string
    status?: AutoVisit["status"][] | AutoVisit["status"]
    query?: string
    dateFrom?: Date
    dateTo?: Date
}


export const useAutoVisitList = ({carrierId, ...filter}: useAutoVisitListOptions) => {
    const handleError = useErrorHandler();
    const {
        data,
        loading,
        error,
        refetch,
        fetchMore: _fetchMore,
    } = useQuery<GetAutoVisitsQueryResult, GetAutoVisitsQueryVars>(GetAutoVisitListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            dateFrom: filter.dateFrom?.getTime(),
            dateTo: filter.dateTo?.getTime(),
            query: filter.query,
            status: filter.status,
            carrierId: carrierId,
            pathBuilder: autoVisitsListPathBuilder,
        },
        skip: carrierId == null,
    });

    const canFetchMore = Boolean(data?.autovisits?.pageInfo?.nextPage) && !loading;
    const fetchMore = useCallback(() => {
        if (!canFetchMore) {
            return;
        }
        _fetchMore({
            variables: {
                page: data.autovisits.pageInfo.nextPage
            },
        }).catch(handleError);
    }, [data, canFetchMore]);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    return {
        data: data?.autovisits?.autovisits,
        loading: loading || error != null,
        fetchMore,
        canFetchMore
    }
}


export function normalizeAutoVisitResult(data: GetAutoVisitQueryResult["autoVisit"]["Autovisit"]) {
    const CargoList = (Array.isArray(data.CargoList) ? data.CargoList : [])?.map(cargo => ({
        ...cargo,
        CargoID: cargo.ID,
        ID: cargo.CargoID,
    }));
    const ServiceList = (Array.isArray(data.ServiceList) ? data.ServiceList : [])?.map(service => {
        const cargo = service.CargoID == "0" ?
            data.CargoList[0] :
            data.CargoList?.find(x => x.CargoID == service.CargoID);
        return {
            ...service,
            ServiceID: service.ID,
            ID: service.ServiceID,
            CargoID: cargo?.ID
        }
    });
    return {
        ...data,
        CargoList,
        ServiceList
    }
}


export const useAutoVisit = (id: string) => {
    const handleError = useErrorHandler();
    const {
        data: rawData,
        loading,
        error,
        refetch,
    } = useQuery<GetAutoVisitQueryResult, GetAutoVisitQueryVars>(GetAutoVisitDocument, {
        variables: {
            id: id
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    const data = useMemo(() => {
        if (!rawData?.autoVisit?.Autovisit) {
            return undefined;
        }
        return normalizeAutoVisitResult(rawData.autoVisit.Autovisit);
    }, [rawData]);

    return {
        data: data,
        loading: loading || error != null
    }
}

export type useCargoListOptions = {
    autoVisitId?: string
    date?: Date
    excludeCargoIds?: string[]
    page?: number
    type?: ("Генеральный груз" | "Насыпной груз" | "КТК груженый" | "КТК порожний")[],
    direction?: CargoV2["direction"][],
    query?: string
}
export const useCargoList = (options: useCargoListOptions) => {
    const {autoVisitId, date, page, direction, query, type, excludeCargoIds} = options;
    const handleError = useErrorHandler();
    const typeMapping: {[key: string]: CargoV2["type"]} = {
        "Генеральный груз": "Генеральный груз",
        "Насыпной груз": "Насыпной груз",
        "КТК груженый": "Контейнер",
        "КТК порожний": "Контейнер"
    };
    const empty = useMemo(() => {
        if (!type) {
            return null;
        } else if (type.includes("КТК груженый") && type.includes("КТК порожний")) {
            return null;
        } else if (type.includes("КТК груженый")) {
            return false;
        } else if (type.includes("КТК порожний")) {
            return true;
        }
    }, [type]);

    const {
        data,
        loading,
        error,
        refetch,
        fetchMore: _fetchMore
    } = useQuery<GetCargoListResult, GetCargoListVars>(GetCargoListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            autoVisitId: autoVisitId,
            excludeIds: excludeCargoIds,
            date: date ? moment(date).toDate().getTime() : null,
            page: page,
            query: query,
            empty: empty,
            direction: direction,
            type: [...new Set(type?.map(item => typeMapping[item]))],
            pathBuilder: GetCargoListPathBuilder
        }
    });

    const canFetchMore = Boolean(data?.cargos?.pageInfo?.nextPage) && !loading;
    const fetchMore = useCallback(() => {
        if (!canFetchMore) {
            return;
        }
        _fetchMore({
            variables: {
                page: data.cargos.pageInfo.nextPage
            },
        }).catch(handleError);
    }, [data, canFetchMore]);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    return {
        data: data?.cargos?.cargos,
        loading: loading || error != null,
        fetchMore,
        canFetchMore
    }
}


export const useVehicleList = () => {
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} = useQuery<GetVehiclesQueryResult, GetVehiclesQueryVars>(GetVehiclesDocument, {
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error])

    return {
        data: data?.vehicles?.vehicles,
        loading: loading || error != null,
    }
}


export const useDriverList = () => {
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} = useQuery<GetDriversQueryResult, GetDriversQueryVars>(GetDriversDocument, {
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    return {
        data: data?.drivers?.drivers,
        loading: loading || error != null,
    }
}


type useTimeSlotsOptions = {
    carrierId: string
    serviceIds: string[]
    cargoIds: string[]
    autoVisitId?: string
    autoVisitDate?: Date
}
export const useTimeSlots = (options: useTimeSlotsOptions) => {
    const {carrierId, serviceIds, cargoIds, autoVisitId, autoVisitDate} = options;
    const handleError = useErrorHandler();
    const skip = !cargoIds?.length || !serviceIds?.length || !autoVisitDate;

    let {data, loading, error, refetch} = useQuery<GetTimeSlotsQueryResult, GetTimeSlotsQueryVars>(GetTimeSlotsDocument, {
        notifyOnNetworkStatusChange: true,
        skip: skip,
        variables: {
            serviceIds: [...new Set(serviceIds)],
            carrierId: carrierId,
            cargoId: cargoIds?.length ? cargoIds[0] : null,
            autoVisitId: autoVisitId,
            autoVisitDate: moment(autoVisitDate).format("YYYY-MM-DD"),
            pathBuilder: GetTimeSlotsPathBuilder,
        }
    });
    const emptyTimeSlots = "По указанному идентификатору груз не найден";

    if (error?.graphQLErrors?.find(x => x.extensions.message == emptyTimeSlots)) {
        error = undefined;
        data = {timeSlots: {TimeSlotList: []}};
    }

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error])

    return {
        data: skip ? [] : data?.timeSlots?.TimeSlotList,
        loading: loading || error != null,
    }
}


export const useCreateAutoVisit = () => {
    const client = useApolloClient();
    const [execute, {
        data,
        error,
        loading,
        called
    }] = useMutation<CreateAutoVisitMutationResult, CreateAutoVisitMutationVars>(CreateAutoVisitDocument, {
        notifyOnNetworkStatusChange: true,
        errorPolicy: "all",
        onCompleted: () => {
            client.cache.evict({id: "ROOT_QUERY", fieldName: "autovisits"});
            client.cache.evict({id: "ROOT_QUERY", fieldName: "cargos"});
        }
    });

    const create = (input: CreateAutoVisitMutationVars["input"]) => {
        return execute({
            variables: {
                input: {
                    ...input,
                }
            },
            refetchQueries: [
                {
                    query: GetCargoListDocument,
                    variables: {
                        carrierId: input.CarrierID,
                        pathBuilder: GetCargoListPathBuilder,
                    }
                }
            ],
            awaitRefetchQueries: true,
        });
    }

    useEffect(() => () => {
        client.cache.evict({id: "ROOT_QUERY", fieldName: "cargos"});
        client.cache.evict({id: "ROOT_QUERY", fieldName: "services"});
        client.cache.evict({id: "ROOT_QUERY", fieldName: "timeSlots"});
        client.cache.evict({id: "ROOT_QUERY", fieldName: "vehicles"});
        client.cache.evict({id: "ROOT_QUERY", fieldName: "drivers"});
        client.cache.evict({id: "ROOT_QUERY", fieldName: "checkPoints"});
    }, []);

    return tuple(create, {
        data: data?.CreateAutoVisit?.Autovisit,
        success: data?.CreateAutoVisit?.Autovisit && error == null && called,
        error: error,
        loading,
    });
}


export const useUpdateAutoVisit = (id: string) => {
    const client = useApolloClient();
    const [execute, {
        data,
        error,
        loading,
        called
    }] = useMutation<UpdateAutoVisitQueryResult, UpdateAutoVisitQueryVars>(UpdateAutoVisitDocument, {
        notifyOnNetworkStatusChange: true,
        errorPolicy: "all",
        onCompleted: () => {
            client.cache.evict({id: "ROOT_QUERY", fieldName: "cargos"});
        }
    });
    const success = called ? !loading && !error && !data.UpdateAutoVisit.ErrorInfo : null;

    const update = (input: UpdateAutoVisitQueryVars["input"]) => {
        return execute({
            variables: {
                input: {
                    ...input,
                },
                id: id
            },
            refetchQueries: [
                {
                    query: GetCargoListDocument,
                    variables: {
                        carrierId: input.CarrierID,
                        pathBuilder: GetCargoListPathBuilder,
                    }
                }
            ],
            awaitRefetchQueries: true
        });
    }

    return tuple(update, {
        data: data?.UpdateAutoVisit?.Autovisit,
        error,
        loading,
        called,
        success
    });
}


export const useCancelAutoVisit = (id: string) => {
    const client = useApolloClient();
    const [execute, {
        data,
        loading,
        error,
        called
    }] = useMutation<CancelAutoVisitResult, CancelAutoVisitVars>(CancelAutoVisitDocument, {
        notifyOnNetworkStatusChange: true,
        errorPolicy: "all",
        onCompleted: () => {
            client.cache.evict({id: "ROOT_QUERY", fieldName: "cargos"});
        }
    });

    const cancel = async () => {
        return execute({
            variables: {
                id: id
            },
            refetchQueries: [
                {
                    query: GetAutoVisitDocument,
                    variables: {
                        id: id
                    }
                },
            ],
            awaitRefetchQueries: true
        });
    }

    return tuple(cancel, {
        data: data?.cancelAutoVisit,
        error,
        loading,
        called,
        success: called && data?.cancelAutoVisit && !loading
    });
}


export const useCurrentCarrierId = () => {
    const [data, loading] = useCurrentUser();

    return tuple(data?.inn, loading);
}


type useCheckPointListOptions = {
    carrierId?: string
    cargoIds?: string[]
    autoVisitId?: string
    autoVisitDate?: Date
    timeslotId?: string
}
export const useCheckPointList = (options: useCheckPointListOptions) => {
    const {cargoIds, timeslotId, carrierId, autoVisitId, autoVisitDate} = options;
    const handleError = useErrorHandler();
    const result = useQuery<GetCheckpointListResult, GetCheckPointListVars>(GetCheckPointListDocument, {
        variables: {
            carrierId,
            cargoIds,
            autoVisitId,
            timeslotId,
            autoVisitDate: moment(autoVisitDate).format("YYYY-MM-DD"),
            pathBuilder: GetCheckPointPathBuilder
        },
        skip: !cargoIds?.length || !timeslotId || !carrierId || !autoVisitDate,
        notifyOnNetworkStatusChange: true,
    });
    const {refetch, called,} = result;
    let {data, error, loading} = result;

    if ((error?.networkError as ServerError)?.result?.ErrorInfo?.Discription == "Не найден свободный КПП.") {
        error = null;
        data = {
            ...data,
            checkPoints: {
                CheckPointList: []
            }
        }
    }

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    return {
        data: data?.checkPoints?.CheckPointList,
        loading: loading || error != null,
        called: called,
    }
}


export const useCargoNecessaryServiceListExecutor = (carrierId: string, autoVisitId?: string) => {
    const apollo = useApolloClient();

    return useCallback(async (cargoId: string) => {
        const {data} = await apollo.query<GetServiceListQueryResult, GetServiceListQueryVars>({
            query: GetServiceListDocument,
            variables: {
                cargoId: cargoId,
                carrierId: carrierId,
                autoVisitId: autoVisitId,
                pathBuilder: GetServiceListPathBuilder,
            }
        });
        return data?.services?.ServiceList?.filter(x => x.Necessary);
    }, [carrierId, apollo]);
}


export const useServiceList = (params: { carrierId: string, cargoId: string, autoVisitId: string }) => {
    const handleError = useErrorHandler();
    const {
        data,
        loading,
        error,
        refetch
    } = useQuery<GetServiceListQueryResult, GetServiceListQueryVars>(GetServiceListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {...params, pathBuilder: GetServiceListPathBuilder},
        skip: params.cargoId == null || params.carrierId == null
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID)
        }
    }, [error]);

    return {
        data: data?.services?.ServiceList,
        loading: !!error || loading,
    }
}